import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, X, Plus, Paperclip, FileText } from "lucide-react";

export default function EmailModal({ quotation, seller, open, onClose, onSuccess }) {
  const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  const [sending, setSending] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [newToEmail, setNewToEmail] = useState("");
  const [newCcEmail, setNewCcEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Update subject and body when quotation or seller changes
  useEffect(() => {
    if (quotation && seller) {
      setSubject(`${seller.seller_name} (${seller.seller_code})`);
    } else if (quotation) {
      setSubject(`${quotation.seller_name || ''} (${quotation.quotation_id || ''})`);
    }
    setBody("Hi Team,\nPFA quotation for generating Sales Order");
  }, [quotation, seller]);

  // Reset state and fetch the admin-managed To/CC recipients whenever the modal opens.
  // Recipients are NEVER hardcoded — they come from Control Panel → Email Configuration.
  useEffect(() => {
    if (open) {
      setAdditionalFiles([]);
      axios.get(`${API}/settings/email-config`, { withCredentials: true })
        .then((res) => {
          setToEmails(res.data?.default_to || []);
          setCcEmails(res.data?.default_cc || []);
        })
        .catch(() => {
          setToEmails([]);
          setCcEmails([]);
          toast.error("Could not load default email recipients");
        });
    }
  }, [open]);

  const addToEmail = () => {
    const e = newToEmail.trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { toast.error("Enter a valid email address"); return; }
    if (toEmails.some((x) => x.toLowerCase() === e.toLowerCase())) { toast.error("Email already added"); return; }
    setToEmails([...toEmails, e]);
    setNewToEmail("");
  };

  const removeToEmail = (email) => {
    setToEmails(toEmails.filter(e => e !== email));
  };

  const addCcEmail = () => {
    const e = newCcEmail.trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { toast.error("Enter a valid email address"); return; }
    if (ccEmails.some((x) => x.toLowerCase() === e.toLowerCase())) { toast.error("Email already added"); return; }
    setCcEmails([...ccEmails, e]);
    setNewCcEmail("");
  };

  const removeCcEmail = (email) => {
    setCcEmails(ccEmails.filter(e => e !== email));
  };


  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB limit per file
    
    for (const file of files) {
      if (file.size > maxSize) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        continue;
      }
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:xxx;base64, prefix
        setAdditionalFiles(prev => [...prev, {
          filename: file.name,
          content_base64: base64,
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    }
    
    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setAdditionalFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSend = async () => {
    if (toEmails.length === 0) {
      toast.error("At least one recipient is required");
      return;
    }

    setSending(true);
    try {
      // Send email - always as a NEW email (no threading)
      await axios.post(
        `${API}/quotations/${quotation.id}/send-email`,
        { 
          to_emails: toEmails,
          cc_emails: ccEmails,
          subject: subject,
          body: body,
          additional_attachments: additionalFiles.length > 0 ? additionalFiles : null
        },
        {
          withCredentials: true
        }
      );

      toast.success("Email sent successfully");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error sending email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="email-modal-title">
            Send Quotation via Email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* To Emails */}
          <div className="space-y-2">
            <Label>To:</Label>
            <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-slate-50 min-h-[40px]">
              {toEmails.map((email, idx) => (
                <span key={`to-${email}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                  {email}
                  <button 
                    type="button"
                    onClick={() => removeToEmail(email)} 
                    className="hover:text-blue-600"
                    data-testid={`remove-to-email-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Add email address"
                value={newToEmail}
                onChange={(e) => setNewToEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToEmail())}
                className="flex-1"
                data-testid="new-to-email-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addToEmail} data-testid="add-to-email-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* CC Emails */}
          <div className="space-y-2">
            <Label>CC:</Label>
            <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-slate-50 min-h-[40px]">
              {ccEmails.map((email, idx) => (
                <span key={`cc-${email}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 text-sm rounded">
                  {email}
                  <button 
                    type="button"
                    onClick={() => removeCcEmail(email)} 
                    className="hover:text-gray-600"
                    data-testid={`remove-cc-email-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Add CC email address"
                value={newCcEmail}
                onChange={(e) => setNewCcEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCcEmail())}
                className="flex-1"
                data-testid="new-cc-email-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addCcEmail} data-testid="add-cc-email-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject:</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              data-testid="email-subject-input"
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label htmlFor="body">Message:</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              data-testid="email-body-input"
            />
          </div>

          {/* Quotation Attachment Info */}
          <div className="p-3 bg-slate-100 rounded text-sm">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-accent" />
              <strong>Quotation Attachment:</strong> Quotation_{quotation?.quotation_id}.xlsx
            </div>
          </div>

          {/* Additional Attachments Section */}
          <div className="space-y-2">
            <Label>Additional Attachments (Optional):</Label>
            
            {/* File List */}
            {additionalFiles.length > 0 && (
              <div className="space-y-2 p-3 bg-slate-50 rounded border">
                {additionalFiles.map((file, idx) => (
                  <div key={`file-${file.filename}-${idx}`} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="flex items-center gap-2">
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{file.filename}</span>
                      <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-destructive hover:text-destructive/80"
                      data-testid={`remove-attachment-${idx}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* File Input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
              data-testid="attach-files-btn"
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Attach Files
            </Button>
            <p className="text-xs text-muted-foreground">
              Supported: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, TXT, CSV (Max 10MB per file)
            </p>
          </div>

          {/* Reply-To Info */}
          <div className="p-2 bg-blue-50 rounded text-xs text-blue-700">
            <strong>Reply-To:</strong> simranmalik@wellversed.in
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={sending} data-testid="cancel-email-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || toEmails.length === 0}
              className="bg-accent hover:bg-accent/90 text-white"
              data-testid="send-email-button"
            >
              <Mail className="h-4 w-4 mr-2" />
              {sending ? "Sending..." : "Send Email"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
